import { useMutation, useQuery } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Session } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { showSessionsDeactivateAllConfirmation } from "./DeactivateAllConfirmation";
import { GET_SESSIONS, SESSION_DELETE_ALL } from "./gql";
import Item from "./Item";
import styles from "./styles.module.css";

const Sessions = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { reportSuccess, onError } = useTypedContext(FlashContext);

  useTitle(`Organization Settings · Sessions · ${accountName}`);

  const { data, loading, error } = useQuery<{ sessions: Session[] }>(GET_SESSIONS);
  const [sessionDeleteAll, { loading: sessionDeleteAllLoading }] = useMutation<{
    deletedSessions: Session[];
  }>(SESSION_DELETE_ALL, { refetchQueries: ["GetSessions"] });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const handleDeactivation = () => {
    const asyncFn = async () => {
      try {
        await sessionDeleteAll();

        reportSuccess({ message: `Sessions were deactivated` });
      } catch (error) {
        onError(error);
      }
    };

    void asyncFn();
  };

  const onModalShow = () => {
    showSessionsDeactivateAllConfirmation({ onConfirm: handleDeactivation });
  };

  return (
    <>
      <PageInfoHeader title="Sessions">
        <Button variant="dangerSecondary" loading={sessionDeleteAllLoading} onPress={onModalShow}>
          Deactivate all sessions
        </Button>
      </PageInfoHeader>

      <div className={styles.wrapper}>
        {loading && <PageLoading />}

        {data?.sessions.map((session) => <Item key={session.id} session={session} />)}
      </div>
    </>
  );
};

export default Sessions;
